import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { faFont, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useDrag } from 'react-dnd'
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import { styled } from '../../assets/theme';

interface IPanelProps {
  children: JSX.Element,
  isActive: boolean,
  canScroll?: boolean,
  title: string,
  id: number,
  handler: string,
  hideHeader?: boolean,
  canPopout?: boolean,
  centerHeader?: boolean,
  phref?: string,
  fontSizeAdjustable?: boolean,
  left: number,
  top: number,
  width: number,
  height: number,
  handleRemoveWidget: (id: number) => void,
  updateLayout: (payload: any) => void,
  zIndex: number
}

export const Panel: React.FunctionComponent<IPanelProps> = ({
  children,
  canScroll = false,
  canPopout,
  centerHeader,
  title,
  left,
  top,
  width,
  height,
  id,
  handler,
  hideHeader,
  phref,
  handleRemoveWidget,
  fontSizeAdjustable,
  updateLayout,
  zIndex
}) => {
  const [panelFontSize, setPanelFontSize] = useState('1');

  // const openNewTab = (e: React.MouseEvent<HTMLDivElement>) => {
  //   window.open(`${phref}/${id}`, '_blank')
  // }

  const removeWidget = (e: React.MouseEvent<HTMLDivElement>) => {
    handleRemoveWidget(id)
  }

  const changeFontSize = (e: React.MouseEvent<HTMLDivElement>) => {
    setPanelFontSize(e.currentTarget.dataset.size || '1')
  }

  const [{ isDragging }, drag, preview] = useDrag({
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    type: 'box',
    item: { id, left, top }
  })

  const resizeStop = (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
    updateLayout([{
      h: Math.round(data.size.height / 25) * 25,
      i: id,
      w: Math.round(data.size.width / 25) * 25
    }])
  }

  return (
    <div style={{ position: 'absolute', top, left, visibility: isDragging ? 'hidden' : 'visible', zIndex }}>
      <div>
    <ResizableBox onResizeStop={resizeStop} width={width} height={height} resizeHandles={['se', 'e', 's']}>

    <PanelWrapper ref={preview} style={{ userSelect: 'none' }} className="test">
      {
        hideHeader ?
        <GripHandle>
          <FontAwesomeIcon className={handler} style={{ cursor: 'pointer', opacity: 0.8 }} size="1x" icon={faGripVertical} title="Drag" />
        </GripHandle>
        :
        <PanelHeader ref={drag} className={handler}>
        <svg viewBox="0 0 406 100" width="80px" height="20px">
        </svg>
          <PanelTitle center={centerHeader}>
            {title}</PanelTitle>
            <div style={{ display: 'grid', gridAutoFlow: 'column', justifyContent: 'end', gridGap: '0.5em' }}>
            {
              fontSizeAdjustable ?
              <div style={{ display: 'grid', gridAutoFlow: 'column', gridGap: '0.25em', height: '100%' }}>
                <FontSizeGroupButton selected={panelFontSize === "0.75"} data-size="0.75" onClick={changeFontSize}>
                  <FontAwesomeIcon style={{ cursor: 'pointer', opacity: 0.8 }} size="xs" icon={faFont} title="Small" />
                </FontSizeGroupButton>
                <FontSizeGroupButton selected={panelFontSize === "1"} data-size="1" onClick={changeFontSize}>
                  <FontAwesomeIcon style={{ cursor: 'pointer', opacity: 0.8 }} size="sm" icon={faFont} title="Medium" />
                </FontSizeGroupButton>
                <FontSizeGroupButton selected={panelFontSize === "1.5"} data-size="1.5" onClick={changeFontSize}>
                  <FontAwesomeIcon style={{ cursor: 'pointer', opacity: 0.8 }} size="lg" icon={faFont} title="Large" />
                </FontSizeGroupButton>
              </div>
              :
              <div />
            }
            {/* <div style={{ visibility: canPopout ? 'inherit' : 'collapse' }} onClick={openNewTab}>
              <FontAwesomeIcon style={{ cursor: 'pointer', opacity: 0.8 }} size="1x" icon={faExternalLinkAlt} title="Open in new tab" />
            </div> */}
            <div onClick={removeWidget}>
              <FontAwesomeIcon style={{ cursor: 'pointer', opacity: 0.8 }} size="1x" icon={faTimesCircle} title="Close" />
            </div>
          </div>
        </PanelHeader>

      }
      <PanelContent canScroll={canScroll} style={{ fontSize: `${panelFontSize}rem` }}>
        {children}
      </PanelContent>
    </PanelWrapper>
    </ResizableBox>
    </div>
    </div>
  )
};

const FontSizeGroupButton = styled.div<{selected?: boolean}>`
  height: 100%;
  width: 25px;
  display: grid;
  align-items: center;
  justify-items: center;
  background-color: ${({ theme, selected }) => selected ? theme.core.alternateBackground: 'initial' };
`;

const GripHandle = styled.div`
  position: absolute;
  left: -16px;
  top: 0px;
  padding: 10px 10px 10px 10px;
  background-color: ${({ theme }) => theme.core.alternateChartBackground};
  /* border-radius: 3px; */
`;

const PanelWrapper = styled.div`
  border: none;
  background-color: ${({ theme }) => theme.core.alternateChartBackground};
  overflow: hidden;
  height : 100%;
`;

const PanelHeader = styled.div`
  height: 32px;
  background-color: ${({ theme }) => theme.core.alternateBackground};
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-gap: 0.5em;
  align-items: center;
  cursor: move;
  padding-right: 10px;
`;

const PanelTitle = styled.span<{center?: boolean}>`
  text-align: ${({ center }) => center ? 'center' : 'inherit' };
  position: ${({ center }) => center ? 'relative' : 'inherit' };
  font-size: 1.4em;
  font-weight: 400;
  opacity: 0;
`;

const PanelContent = styled.div<{canScroll: boolean}>`
  overflow: ${({ canScroll }) => (canScroll ? 'auto' : 'hidden')};
  height: calc(100% - 27px);
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &:hover::-webkit-scrollbar-thumb {
    visibility: visible;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(212, 221, 232, 0.4);
    visibility: hidden;
    border-radius: 22px;
  }
  &:hover::-webkit-scrollbar-track {
    visibility: visible;
  }
  &::-webkit-scrollbar-track {
      background: transparent;
      visibility: hidden;
  }
`;
